import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import QuizProgressBar from "./QuizProgressBar";
import QuizNavigation from "./QuizNavigation";
import TextField from "./questions/TextField";
import MultipleChoice from "./questions/MultipleChoice";
import RatingScale from "./questions/RatingScale";
import RankingDragDrop from "./questions/RankingDragDrop";
import "../../assets/styling/quiz.css";

function getStorageKeys(quizSlug) {
  const safeSlug =
    typeof quizSlug === "string" && quizSlug.trim()
      ? quizSlug.trim().replace(/[^a-zA-Z0-9_-]/g, "_")
      : "default";
  const prefix = `tbb_quiz_${safeSlug}`;
  return {
    LS_SESSION: `${prefix}_sessionId`,
    LS_ANSWERS: `${prefix}_answers`,
    LS_STEP: `${prefix}_currentStep`,
    LS_COMPLETE: `${prefix}_completed`,
  };
}

// Question types that auto-advance (no OK button needed)
const AUTO_ADVANCE_TYPES = new Set([]);

function isAutoAdvance(question, answer) {
  if (AUTO_ADVANCE_TYPES.has(question.type)) return true;
  // Single-select multiple choice
  if (question.type === "multiple_choice" && !question.multiSelect && answer) return true;
  return false;
}

function hasAnswer(question, answer) {
  if (answer == null) return false;
  if (typeof answer === "string") return answer.trim().length > 0;
  if (typeof answer === "number") return true;
  if (Array.isArray(answer)) return answer.length > 0;
  return Boolean(answer);
}

function interpolateAnswers(text, answers) {
  if (typeof text !== "string") return text || "";
  return text.replace(/\{answer:(\w+)\}/g, (match, questionId) => {
    const val = answers[questionId];
    if (val == null) return match;
    if (Array.isArray(val)) return val.join(", ");
    return String(val);
  });
}

const QUESTION_COMPONENTS = {
  text_field: TextField,
  multiple_choice: MultipleChoice,
  rating_scale: RatingScale,
  ranking_drag_drop: RankingDragDrop,
};

const QuizContainer = ({ questions, city, persona, quizSlug }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [phase, setPhase] = useState("active"); // "active" | "submitting"
  const [animClass, setAnimClass] = useState("tbb-quiz__question--enter-forward");
  const autoAdvanceTimer = useRef(null);
  const prevStepRef = useRef(0);
  const { LS_SESSION, LS_ANSWERS, LS_STEP, LS_COMPLETE } = getStorageKeys(quizSlug);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Init: restore from localStorage or start fresh
  useEffect(() => {
    const savedComplete = localStorage.getItem(LS_COMPLETE);
    const savedSession = localStorage.getItem(LS_SESSION);

    if (savedComplete === "1" || !savedSession) {
      // Clear old data and start fresh
      localStorage.removeItem(LS_SESSION);
      localStorage.removeItem(LS_ANSWERS);
      localStorage.removeItem(LS_STEP);
      localStorage.removeItem(LS_COMPLETE);

      const newId = crypto.randomUUID();
      setSessionId(newId);
      localStorage.setItem(LS_SESSION, newId);

      // Fire-and-forget init
      fetch("/api/quiz/init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          sessionId: newId,
          quizSlug: quizSlug || "ai-readiness",
          city: city || "sanantonio",
          persona: persona || "customer_service_rep",
        }),
      }).catch(() => {});
    } else {
      // Restore session
      setSessionId(savedSession);
      try {
        const savedAnswers = JSON.parse(localStorage.getItem(LS_ANSWERS) || "{}");
        setAnswers(savedAnswers);
      } catch {
        setAnswers({});
      }
      const savedStep = parseInt(localStorage.getItem(LS_STEP) || "0", 10);
      setCurrentStep(savedStep);
      prevStepRef.current = savedStep;
    }
  }, [LS_ANSWERS, LS_COMPLETE, LS_SESSION, LS_STEP, city, persona, quizSlug]);

  const currentQuestion = questions[currentStep];
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;
  const answered = currentQuestion ? hasAnswer(currentQuestion, currentAnswer) : false;
  const shouldAutoAdvance = currentQuestion ? isAutoAdvance(currentQuestion, currentAnswer) : false;

  // Persist answers + step to localStorage
  const persistLocal = useCallback(
    (newAnswers, step) => {
      localStorage.setItem(LS_ANSWERS, JSON.stringify(newAnswers));
      localStorage.setItem(LS_STEP, String(step));
    },
    [LS_ANSWERS, LS_STEP]
  );

  // Fire progress to backend
  const fireProgress = useCallback(
    (questionId, questionType, value, step) => {
      if (!sessionId) return;
      fetch("/api/quiz/progress", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          sessionId,
          questionId,
          questionType,
          value,
          currentStep: step,
        }),
      }).catch(() => {});
    },
    [sessionId]
  );

  const navigateToResults = useCallback(
    (targetSessionId) => {
      if (!targetSessionId) return;
      navigate(`/results?sessionId=${encodeURIComponent(targetSessionId)}`);
    },
    [navigate]
  );

  const goToStep = useCallback(
    (nextStep, direction) => {
      const dirSuffix = direction === 1 ? "forward" : "back";
      // Exit current
      setAnimClass(`tbb-quiz__question--exit-${dirSuffix}`);
      setTimeout(() => {
        setCurrentStep(nextStep);
        prevStepRef.current = nextStep;
        setAnimClass(`tbb-quiz__question--enter-${dirSuffix}`);
      }, 300);
    },
    []
  );

  const handleNext = useCallback(() => {
    if (phase !== "active") return;

    if (currentStep < questions.length - 1) {
      const nextStep = currentStep + 1;
      persistLocal(answers, nextStep);
      goToStep(nextStep, 1);
    } else {
      // Last question — submit
      setPhase("submitting");
      localStorage.setItem(LS_COMPLETE, "1");

      fetch("/api/quiz/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, answers }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then(() => {
          navigateToResults(sessionId);
        })
        .catch(() => {
          navigateToResults(sessionId);
        });
    }
  }, [
    phase,
    currentStep,
    questions.length,
    answers,
    sessionId,
    persistLocal,
    goToStep,
    navigateToResults,
  ]);

  const handleBack = useCallback(() => {
    if (currentStep > 0 && phase === "active") {
      const prevStep = currentStep - 1;
      persistLocal(answers, prevStep);
      goToStep(prevStep, -1);
    }
  }, [currentStep, phase, answers, persistLocal, goToStep]);

  const handleAnswer = useCallback(
    (value) => {
      if (!currentQuestion) return;

      const newAnswers = { ...answers, [currentQuestion.id]: value };
      setAnswers(newAnswers);
      persistLocal(newAnswers, currentStep);
      fireProgress(currentQuestion.id, currentQuestion.type, value, currentStep);

      // Auto-advance for certain types
      if (isAutoAdvance(currentQuestion, value)) {
        if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
        autoAdvanceTimer.current = setTimeout(() => {
          // Need to advance with the latest step
          if (currentStep < questions.length - 1) {
            const nextStep = currentStep + 1;
            const updatedAnswers = { ...answers, [currentQuestion.id]: value };
            localStorage.setItem(LS_ANSWERS, JSON.stringify(updatedAnswers));
            localStorage.setItem(LS_STEP, String(nextStep));
            goToStep(nextStep, 1);
          } else {
            // Submit
            setPhase("submitting");
            localStorage.setItem(LS_COMPLETE, "1");
            const updatedAnswers = { ...answers, [currentQuestion.id]: value };

            fetch("/api/quiz/submit", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ sessionId, answers: updatedAnswers }),
            })
              .then((r) => (r.ok ? r.json() : null))
              .then(() => {
                navigateToResults(sessionId);
              })
              .catch(() => {
                navigateToResults(sessionId);
              });
          }
        }, 400);
      }
    },
    [
      currentQuestion,
      answers,
      currentStep,
      questions.length,
      sessionId,
      persistLocal,
      fireProgress,
      goToStep,
      navigateToResults,
    ]
  );

  // Cleanup auto-advance timer
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    };
  }, []);

  // Enter key handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && phase === "active" && answered && !shouldAutoAdvance) {
        e.preventDefault();
        handleNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [phase, answered, shouldAutoAdvance, handleNext]);

  if (phase === "submitting") {
    return (
      <div className="tbb-quiz">
        <QuizProgressBar currentStep={questions.length - 1} totalSteps={questions.length} />
        <div className="tbb-quiz__body">
          <div className="tbb-quiz__question tbb-quiz__question--enter-forward">
            <div className="tbb-quiz__number-badge">{questions.length}</div>
            <h2 className="tbb-quiz__prompt">Preparing your results...</h2>
            <p className="tbb-quiz__subtitle">
              We&apos;re locking in your quiz outcome and taking you to your results page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestion) return null;

  const QuestionComponent = QUESTION_COMPONENTS[currentQuestion.type];
  if (!QuestionComponent) return null;

  const interpolatedPrompt = interpolateAnswers(currentQuestion.prompt, answers);
  const interpolatedSubtitle = interpolateAnswers(currentQuestion.subtitle, answers);

  // Hide OK button for auto-advance types that already have an answer
  const showOk = !shouldAutoAdvance;

  return (
    <div className="tbb-quiz">
      <QuizProgressBar currentStep={currentStep} totalSteps={questions.length} />

      <div className="tbb-quiz__body">
        <div key={currentStep} className={`tbb-quiz__question ${animClass}`}>
          {currentQuestion.questionImage?.url && (
            <img
              className="tbb-quiz__question-image"
              src={currentQuestion.questionImage.url}
              alt={currentQuestion.questionImage.alt || ""}
              loading="lazy"
            />
          )}
          <div className="tbb-quiz__number-badge">{currentStep + 1}</div>
          <h2 className="tbb-quiz__prompt">{interpolatedPrompt}</h2>
          {interpolatedSubtitle && (
            <p className="tbb-quiz__subtitle">{interpolatedSubtitle}</p>
          )}
          <QuestionComponent
            question={currentQuestion}
            answer={currentAnswer}
            onAnswer={handleAnswer}
            answers={answers}
          />
        </div>
      </div>

      {showOk && (
        <QuizNavigation
          canGoBack={currentStep > 0}
          canSubmit={answered}
          onBack={handleBack}
          onNext={handleNext}
        />
      )}

      {!showOk && currentStep > 0 && (
        <div className="tbb-quiz__nav">
          <button
            type="button"
            className="tbb-quiz__back"
            onClick={handleBack}
            aria-label="Go back"
          >
            &#8249;
          </button>
          <div style={{ flex: 1 }} />
        </div>
      )}
    </div>
  );
};

export default QuizContainer;
